import React, { useState, useEffect, useRef } from "react";
import CustomModal from "components/customModal";
import PrimaryButton from "components/PrimaryButton";
import LinkButton from "components/LinkButton";
import { useTranslation } from "react-i18next";
import styles from "styles/modules/CopyWithName.module.scss";
import FooterButtons from "components/FooterButtons";
import ModuleListCard from "components/businessrules/ModuleListCard";
import CustomTextField from "components/CustomTextField";
import { CopyCardSourceModule } from "./CopyCard";
import blueTick from "images/Tickblue.svg";

const CopyWithName = ({
  sourceModule,
  handleChildClose,
  onClose,
  setUpdatedNames,
  setCloneLoading,
  makeCloneCopy,
}) => {
  const extractModules = (ruleCondition) => {
    const rule = sourceModule?.businessRules?.find(
      (r) => r.ruleCondition === ruleCondition
    );
    if (!rule || !rule.targetModules) return [];
    return rule.targetModules.map((mod, idx) => ({
      id: mod.targetModuleId || idx + 1,
      moduleName: mod.targetModuleName || "",
      moduleDamName: mod.targetModuleDamName || "",
      mlrStatus: mod.targetModuleMlrStatus || "",
      componentThumbnailUrl: mod.targetModuleComponentThumbnailUrl || "",
      isSelected: true,
      sourceModuleDamName: mod.targetModuleDamName || "",
      sourceModuleNextUniqueModuleCode: mod.targetNextUniqueModuleCode,
      ruleCondition: ruleCondition,
      sourceModuleId: mod.targetModuleId || ``,
      cmsdDocUrl: mod.cmsdDocUrl || "",
    }));
  };

  const initialSource = sourceModule
    ? [
        {
          id: 1,
          moduleName: sourceModule.moduleName,
          moduleDamName: sourceModule.moduleDamName,
          mlrStatus: sourceModule.mlrStatus,
          componentThumbnailUrl: sourceModule.componentThumbnailUrl || "",
          isSelected: true,
          cmsdDocUrl: sourceModule.cmsdDocUrl || "",
        },
      ]
    : [];

  const initialMustExist = extractModules("MustExistWith");
  const initialRelated = extractModules("Optional");

  const [moduleData, setModuleData] = useState({
    source: {
      items: initialSource,
      selectedIds: initialSource.map((item) => item.id),
      isEdited: false,
    },
    mustExist: {
      items: initialMustExist,
      selectedIds: initialMustExist.map((item) => item.id),
      isEdited: false,
    },
    related: {
      items: initialRelated,
      selectedIds: initialRelated.map((item) => item.id),
      isEdited: false,
    },
  });

  const [loading, setLoading] = useState({
    source: false,
    mustExist: false,
    related: false,
  });

  const [activeStep, setActiveStep] = useState(1);
 
  const hasMustExist = initialMustExist.length > 0;
  const hasRelated = initialRelated.length > 0;
  const [completedSteps, setCompletedSteps] = useState([]);
  const { t } = useTranslation();

  const fetchModuleData = async (type) => {
    switch (type) {
      case "source": {
        if (sourceModule && sourceModule.moduleName) {
          const item = {
            id: 1,
            moduleName: sourceModule.moduleName,
            moduleDamName: sourceModule.moduleDamName,
            mlrStatus: sourceModule.mlrStatus || "",
            componentThumbnailUrl: sourceModule.componentThumbnailUrl || "",
            isSelected: true,
          };
          return { items: [item], selectedIds: [item.id] };
        }
        // fallback
        return { items: [], selectedIds: [] };
      }
      case "mustExist": {
        const rule = sourceModule?.businessRules?.find(
          (r) => r.ruleCondition === "MustExistWith"
        );
        if (rule && rule.targetModules && rule.targetModules.length > 0) {
          const items = rule.targetModules.map((mod, idx) => ({
            id: mod.targetModuleId || idx + 1,
            sourceModuleId: mod.targetModuleId,
            moduleName: mod.targetModuleName || "",
            sourceModuleNextUniqueModuleCode: mod.targetNextUniqueModuleCode,
            sourceModuleDamName: mod.targetModuleDamName || "",
            ruleCondition: "MustExistWith",
            mlrStatus: mod.targetModuleMlrStatus || "",
            componentThumbnailUrl: mod.targetModuleComponentThumbnailUrl || "",
            isSelected: true,
            cmsdDocUrl: mod.cmsdDocUrl || "",
          }));
          return { items, selectedIds: items.map((item) => item.id) };
        }
       
        return { items: [], selectedIds: [] };
      }
      case "related": {
        const rule = sourceModule?.businessRules?.find(
          (r) => r.ruleCondition === "Optional"
        );
        if (rule && rule.targetModules && rule.targetModules.length > 0) {
          const items = rule.targetModules.map((mod, idx) => ({
            id: mod.targetModuleId || idx + 1,
            sourceModuleId: mod.targetModuleId,
            moduleName: mod.targetModuleName || "",
            sourceModuleNextUniqueModuleCode: mod.targetNextUniqueModuleCode,
            sourceModuleDamName: mod.targetModuleDamName || "",
            ruleCondition: "Optional",
            mlrStatus: mod.targetModuleMlrStatus,
            componentThumbnailUrl: mod.targetModuleComponentThumbnailUrl || "",
            isSelected: true,
            cmsdDocUrl: mod.cmsdDocUrl || "",
          }));
          return { items, selectedIds: items.map((item) => item.id) };
        }

        return { items: [], selectedIds: [] };
      }
      default:
        return { items: [], selectedIds: [] };
    }
  };

  useEffect(() => {
    const type =
      activeStep === 1 ? "source" : activeStep === 2 ? "mustExist" : "related";

    if (moduleData[type].items.length === 0) {
      setLoading((prev) => ({ ...prev, [type]: true }));

      fetchModuleData(type).then(({ items, selectedIds }) => {
        setModuleData((prev) => ({
          ...prev,
          [type]: {
            items,
            selectedIds,
            isEdited: false,
          },
        }));
        setLoading((prev) => ({ ...prev, [type]: false }));
      });
    }
  }, [activeStep, sourceModule]);

  const validateStep = (stepType) => {
    const items = moduleData[stepType].items;    
    if (stepType === "source") {
      const sourceItem = items[0];
      return (
        sourceItem &&
        sourceItem.moduleName &&
        sourceItem.moduleName.trim() !== ""
      );
    }    
    const selectedItems = items.filter((item) => item.isSelected);
    return selectedItems.every(
      (item) => item.moduleName && item.moduleName.trim() !== ""
    );
  };

  const validateAllCompletedSteps = () => {
    if (!validateStep("source")) return false;
    if (completedSteps.includes(2) || moduleData.mustExist.items.length > 0) {
      if (!validateStep("mustExist")) return false;
    }     
    if (completedSteps.includes(3) || moduleData.related.items.length > 0) {
      if (!validateStep("related")) return false;
    }
    
    return true;
  };

  const handleCloneClick = () => {
    const currentType =
      activeStep === 1 ? "source" : activeStep === 2 ? "mustExist" : "related";
    if (!validateStep(currentType)) {
      // Don't proceed if current step is not valid
      return;
    }

    if (!completedSteps.includes(activeStep)) {
      setCompletedSteps([...completedSteps, activeStep]);
    }

    const getNextStep = () => {
      if (activeStep === 1) {
        if (hasMustExist) return 2;
        if (hasRelated) return 3;
        return null; 
      }
      if (activeStep === 2) {
        if (hasRelated) return 3;
        return null; 
      }
      return null; 
    };

    const nextStep = getNextStep();
    
    if (nextStep) {     
      setActiveStep(nextStep);
    } else {
      if (!validateAllCompletedSteps()) {
        if (!validateStep("source")) {
          setActiveStep(1);
        } else if (
          !validateStep("mustExist") &&
          moduleData.mustExist.items.length > 0
        ) {
          setActiveStep(2);
        } else if (
          !validateStep("related") &&
          moduleData.related.items.length > 0
        ) {
          setActiveStep(3);
        }
        return;
      }
      
      const endSelected = {
        source: moduleData.source.items.filter((item) => item.isSelected),
        mustExist: moduleData.mustExist.items.filter((item) => item.isSelected),
        related: moduleData.related.items.filter((item) => item.isSelected),
      };
      
      setCloneLoading(true);
      makeCloneCopy({ ...endSelected });
    }
  };

  const handleModuleChange = (moduleId, changedName) => {
    const type =
      activeStep === 1 ? "source" : activeStep === 2 ? "mustExist" : "related";

    setModuleData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        items: prev[type].items.map((item) =>
          item.id === moduleId ? { ...item, moduleName: changedName } : item
        ),
        isEdited: true,
      },
    }));
  };

  const handleSelectionChange = (moduleId, isSelected) => {
    const type =
      activeStep === 1 ? "source" : activeStep === 2 ? "mustExist" : "related";

    setModuleData((prev) => {
      const newSelectedIds = isSelected
        ? [...prev[type].selectedIds, moduleId]
        : prev[type].selectedIds.filter((id) => id !== moduleId);

      return {
        ...prev,
        [type]: {
          ...prev[type],
          items: prev[type].items.map((item) =>
            item.id === moduleId ? { ...item, isSelected } : item
          ),
          selectedIds: newSelectedIds,
          isEdited: true,
        },
      };
    });
  };

  const renderStepperItem = (stepNumber, type) => {
    // Grey out if no data for this step
    const isDisabled =
      (type === "mustExist" && moduleData.mustExist.items.length === 0) ||
      (type === "related" && moduleData.related.items.length === 0);
    const isActive = activeStep === stepNumber;
    const isCompleted = completedSteps.includes(stepNumber);
    const count = moduleData[type].selectedIds.length;

    return (
      <span
        className={`${styles.leftSectionSpan} ${
          isActive ? styles.activeStep : ""
        } ${isDisabled ? styles.disabledStep : ""}`}
        style={isDisabled ? { pointerEvents: "none", opacity: 0.5 } : {}}
        onClick={() => !isDisabled && setActiveStep(stepNumber)}
      >
        <span>
          {t(`module.${type}Module`)} ({count})
        </span>
        {isCompleted && (
          <img
            src={blueTick}
            alt="tick"
            className="info-icon"
            style={{ marginRight: "6px" }}
          />
        )}
      </span>
    );
  };

  const getCurrentModuleData = () => {
    const type =
      activeStep === 1 ? "source" : activeStep === 2 ? "mustExist" : "related";
    return {
      items: moduleData[type].items,
      type,
      isEdited: moduleData[type].isEdited,
      isLoading: loading[type],
    };
  };

  const isPrimaryButtonDisabled = () => {
    const currentType =
      activeStep === 1 ? "source" : activeStep === 2 ? "mustExist" : "related";
    
    if (activeStep === 1) {
      const sourceItem = moduleData.source.items[0];
      return (
        !sourceItem ||
        !sourceItem.moduleName ||
        sourceItem.moduleName.trim() === ""
      );
    }
    
    const currentItems = moduleData[currentType].items;
    const hasEmptyNameInSelected = currentItems.some(
      (item) =>
        item.isSelected && (!item.moduleName || item.moduleName.trim() === "")
    );
    
    return hasEmptyNameInSelected;
  };
  return (
    <CustomModal
      displayModal={true}
      hideModal={() => handleChildClose(false)}
      size={"copyName"}
      dilogClassName="alertModalSmall"
      enforceFocus={true}
      backdrop={"static"}
      keyboard={false}
    >
      <article className={styles.copyheader}>
        <span className={styles.leftHeaderOne}>
          {t("module.moduleNames").toUpperCase()}
        </span>
        <span className={styles.leftHeaderTwo}>
          {t("module.moduleProceed")}
        </span>
      </article>
      <div className={styles.dividerTop}></div>
      <article className={styles.copycontent}>
        <div className={styles.contLeft}>
          <article>
            {renderStepperItem(1, "source")}
            {renderStepperItem(2, "mustExist")}
            {renderStepperItem(3, "related")}
          </article>
        </div>

        <div className={styles.contRight}>
          <article>
            <div className={styles.namechangecont}>
              <CopyCardSourceModule
                moduleData={getCurrentModuleData()}
                ModuleListCard={ModuleListCard}
                onModuleChange={handleModuleChange}
                onSelectionChange={handleSelectionChange}
                currentStep={activeStep}
                                
              />
            </div>
          </article>
        </div>
      </article>
      <article>
        <FooterButtons
          primaryButton={
            <PrimaryButton
              title={t("module.cloneModule").toUpperCase()}
              handleClick={handleCloneClick}
              style={{ marginTop: "20px", marginRight: "4px" }}
              disabled={isPrimaryButtonDisabled()}
            />
          }
          secondaryButton={
            <LinkButton title={"Cancel".toUpperCase()} handleClick={onClose} />
          }
          size="md"
        />
      </article>
    </CustomModal>
  );
};

export default CopyWithName;
